<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { DriverStore } from '../pinia/Driver'
import emitter from '../utils/emitter'
import { dismissNotify, notify, useNotify } from '../composables/useNotify'
import benefits from '../configs/premium.json'
import NavRailItem from '../components/NavRailItem.vue'
import WeeklyDropDialog from '../components/WeeklyDropDialog.vue'
import PremiumForm from '../components/premiumForm.vue'
import PaymentPrice from '../components/PaymentPrice.vue'

const Driver = DriverStore()
const route = useRoute()
const { proxy } = getCurrentInstance()
const dialog = ref(false)
const weeklyDialog = ref(false)
const tab = ref('1')
const weeklyAvailable = ref(false)
const { state } = useNotify()
let shownId = null
let timer
const premium = computed(() => String(Driver.user?.premium) === '1')
const pulse = computed(() => premium.value && weeklyAvailable.value)
const costs = [
  { icon: 'mdi-server', title: 'Main server', copy: 'Dedicated gameplay hosting', amount: '€50/mo' },
  { icon: 'mdi-email', title: 'Email service', copy: 'Account & event mail', amount: '€16/mo' },
  { icon: 'mdi-test-tube', title: 'Test server', copy: 'Feature testing before release', amount: '€15/mo' },
  { icon: 'mdi-cloud', title: 'CDN', copy: 'Fast worldwide delivery', amount: '€1/mo' },
]

function dropId(year, week) {
  return `premium-drop-${year}-${week}`
}
function hideDropNotice() {
  if (state.id != null && shownId != null && state.id === shownId) dismissNotify()
  shownId = null
}
function showDropNotice(status) {
  if (!status?.available || status.year == null || status.week == null) {
    hideDropNotice()
    return
  }
  const id = dropId(status.year, status.week)
  shownId = id
  notify({
    id,
    title: 'Weekly Drop',
    message: 'Your Premium weekly drop is available — click the star or <button type="button" class="nr-notify-action" data-notify-action="open-weekly-drop">here</button> to claim it.',
    type: 'warning',
    icon: 'fa6-solid:star',
    html: true,
    persistent: true,
  })
}
async function checkStatus() {
  if (!Driver.logged || !premium.value) {
    weeklyAvailable.value = false
    hideDropNotice()
    return
  }
  try {
    const status = await proxy.$api({ serviceName: 'premiumDrop', methodName: 'weeklyDropStatus', parameters: [] })
    weeklyAvailable.value = Boolean(status?.available)
    showDropNotice(status)
  } catch {
    weeklyAvailable.value = false
    hideDropNotice()
  }
}
function openPremium() {
  if (pulse.value) {
    weeklyDialog.value = true
    return
  }
  dialog.value = true
}
function openDrop() {
  if (pulse.value) weeklyDialog.value = true
}
function claimed() {
  weeklyAvailable.value = false
  hideDropNotice()
}

onMounted(() => {
  if (route.path === '/premium') dialog.value = true
  checkStatus()
  timer = window.setInterval(checkStatus, 60000)
  emitter.on('open-weekly-drop', openDrop)
})
onBeforeUnmount(() => {
  window.clearInterval(timer)
  emitter.off('open-weekly-drop', openDrop)
})
watch(() => route.path, (path) => { if (path === '/premium') dialog.value = true })
watch(() => [Driver.logged, Driver.user?.premium, Driver.persona?.ID], () => {
  checkStatus()
  if (premium.value && tab.value === '3') tab.value = '1'
})
</script>

<template><NavRailItem variant="premium" icon="star" label="Premium" :active="route.path === '/premium'" :pulse="pulse" @click="openPremium" />
  <WeeklyDropDialog v-model="weeklyDialog" mode="premium" @claimed="claimed" />
  <v-dialog v-model="dialog" width="520" scrollable transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar class="premium-toolbar">
        <v-icon class="mr-2">mdi-star</v-icon>
        <div class="toolbar-content">
          <v-toolbar-title class="toolbar-main-title">Premium</v-toolbar-title>
          <div class="toolbar-subtitle">Support the server and unlock exclusive benefits</div>
        </div>
        <v-spacer />
        <template #extension>
          <v-tabs v-model="tab" centered>
            <v-tab class="premium-tab" value="1"><v-icon size="18">mdi-information</v-icon><span>About</span></v-tab>
            <v-tab class="premium-tab" value="2"><v-icon size="18">mdi-gift</v-icon><span>Benefits</span></v-tab>
            <v-tab v-if="Driver.logged && !premium" class="premium-tab" value="3"><v-icon size="18">mdi-cart</v-icon><span>Buy</span></v-tab>
          </v-tabs>
        </template>
      </v-toolbar>
      <v-card-text>
        <v-window v-model="tab">
          <v-window-item value="1">
            <div class="settings-tab-page">
              <div class="settings-tab-header">
                <div class="header-icon"><v-icon>mdi-information</v-icon></div>
                <div class="header-content">
                  <h3 class="page-title">What is Premium?</h3>
                  <p class="page-subtitle">One-time support that keeps NightRiderz online.</p>
                </div>
              </div>
              <p class="dialog-lead">NightRiderz is community-driven. Premium helps cover monthly infrastructure costs while unlocking lifetime benefits.</p>
              <div class="section-list">
                <div v-for="cost in costs" :key="cost.title" class="section-row">
                  <div class="section-body cost-row">
                    <v-icon size="20">{{ cost.icon }}</v-icon>
                    <div class="cost-copy"><strong>{{ cost.title }}</strong><span>{{ cost.copy }}</span></div>
                    <span class="cost-value">{{ cost.amount }}</span>
                  </div>
                </div>
              </div>
              <div class="summary-grid">
                <div class="summary-tile">
                  <span class="summary-label">Monthly costs</span>
                  <strong class="summary-value">€82</strong>
                </div>
                <div class="summary-tile accent">
                  <span class="summary-label">Premium</span>
                  <strong class="summary-value"><PaymentPrice item-id="premium" /></strong>
                  <span class="summary-note">One-time · lifetime</span>
                </div>
              </div>
            </div>
          </v-window-item>
          <v-window-item value="2">
            <div class="settings-tab-page">
              <div class="settings-tab-header">
                <div class="header-icon"><v-icon>mdi-gift</v-icon></div>
                <div class="header-content">
                  <h3 class="page-title">Benefits</h3>
                  <p class="page-subtitle">Everything included with Premium.</p>
                </div>
              </div>
              <div class="section-list">
                <div v-for="benefit in benefits" :key="benefit.title" class="section-row">
                  <div class="section-body benefit-row">
                    <span class="benefit-icon"><v-icon size="18">{{ `mdi-${benefit.icone}` }}</v-icon></span>
                    <div><strong>{{ benefit.title }}</strong><p v-html="benefit.text" /></div>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
          <v-window-item v-if="Driver.logged && !premium" value="3"><PremiumForm /></v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog></template>

<style scoped>
.toolbar-content {
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  gap:2px}
.toolbar-main-title {
  font-size:1.05rem!important;
  font-weight:800!important;
  line-height:1.2!important}
.toolbar-subtitle {
  font-size:.78rem;
  color:var(--mute);
  line-height:1.2}
.dialog-lead {
  margin:0 0 14px;
  color:var(--mute);
  font-size:.86rem;
  line-height:1.45}
.cost-row, .benefit-row {
  display:flex;
  align-items:center;
  gap:12px}
.cost-copy, .benefit-row > div:last-child {
  flex:1;
  min-width:0}
.cost-copy strong, .benefit-row strong {
  display:block;
  font-size:.9rem;
  font-weight:700}
.cost-copy span, .benefit-row p {
  display:block;
  margin:2px 0 0;
  color:var(--mute);
  font-size:.78rem;
  line-height:1.35}
.cost-value {
  flex-shrink:0;
  font-size:.86rem;
  font-weight:800}
.summary-grid {
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:10px;
  margin-top:14px}
.summary-tile {
  display:flex;
  flex-direction:column;
  gap:4px;
  padding:14px;
  border-radius:10px;
  border:1px solid var(--line);
  background:var(--glass)}
.summary-tile.accent {
  border-color:#ffc10747;
  background:#ffc10714}
.summary-label {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.06em;
  text-transform:uppercase;
  color:var(--mute)}
.summary-value {
  font-size:1.2rem;
  font-weight:800}
.summary-note {
  font-size:.72rem;
  color:var(--mute)}
.benefit-icon {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
  width:36px;
  height:36px;
  border-radius:9px;
  background:var(--glass-strong)}
.premium-tab, .premium-tab .v-btn__content {
  gap:8px!important}
@media (max-width: 520px) {
.summary-grid {
  grid-template-columns:1fr}

}
</style>
